/**
 * Elite Batch 11 Part 2 — Workflow Templates 968–987
 * Community, opt-in, promotions, ads, personalization, retargeting, engagement,
 * events, exit-intent, and expansion revenue workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    {
      sendSmsDetails: {
        to,
        message,
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotUpdateNode(
  id: string,
  label: string,
  y: number,
  properties: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "PATCH",
        url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "properties", value: properties }],
        variables: [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.lastmodifieddate",
            customName: "hubspot_updated_at",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function sendgridNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  htmlContent: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.sendgrid.com/v3/mail/send",
        headers: [
          { key: "Authorization", value: "Bearer {sendgrid_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "personalizations", value: `[{"to":[{"email":"${to}"}]}]` },
          {
            key: "from",
            value: '{"email":"{from_email}","name":"{company_name}"}',
          },
          { key: "subject", value: subject },
          {
            key: "content",
            value: `[{"type":"text/html","value":"${htmlContent}"}]`,
          },
        ],
        variables: [
          { name: "statusCode", customName: "sg_status", type: "number" },
        ],
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 11 PART 2 — TEMPLATES 968–987
// =============================================================================

export const ELITE_BATCH11_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 968. DISCOURSE TOPIC DRAFT QUEUE (7 nodes)
  // ===================================================================

  "Discourse Topic Draft Queue": {
    summary:
      "Receives topic requests via webhook, researches trending angles with SerpAPI, composes the topic with AI, scans for policy compliance, routes through an approval gate, publishes the approved topic to Discourse, and logs the result to Google Sheets.",
    nodes: [
      trigger("Webhook: Topic request received", "webhook_app", {
        source: "discourse",
        event: "topic.request",
        howItStarts:
          "A topic request is submitted via webhook containing the desired subject, target category, and any reference materials for the new Discourse topic",
        setup:
          "Configure Discourse webhook to fire on topic request events. Provide SerpAPI key for content research. Set up Discourse API credentials with topic creation permissions.",
        inputFields: [],
      }),

      // 2. Content research (SerpAPI)
      serpApiNode(
        "2",
        "SerpAPI: Research trending angles for topic",
        280,
        "{webhook_payload}",
        [
          {
            name: "organic_results[0].title",
            customName: "top_result_title",
            type: "string",
          },
          {
            name: "organic_results[0].snippet",
            customName: "top_result_snippet",
            type: "string",
          },
          {
            name: "organic_results[1].title",
            customName: "second_result_title",
            type: "string",
          },
          {
            name: "organic_results[1].snippet",
            customName: "second_result_snippet",
            type: "string",
          },
          {
            name: "related_questions",
            customName: "related_questions_json",
            type: "string",
          },
        ],
      ),

      // 3. AI topic composer
      aiNode(
        "3",
        "AI: Compose Discourse topic draft",
        520,
        "You are a community content strategist. Write engaging Discourse forum topics that spark discussion. Use research data to add depth and relevance. Return JSON with topic_title, topic_body (Markdown formatted), category_slug, tags (comma-separated), and estimated_engagement (low/medium/high).",
        "Compose a Discourse topic based on this request payload: {webhook_payload}. Research context: Top result: {top_result_title} - {top_result_snippet}. Second result: {second_result_title} - {second_result_snippet}. Related questions: {related_questions_json}. Write a compelling, discussion-provoking topic with proper Markdown formatting.",
        [
          { field: "topic_title", as: "topic_title", type: "string" },
          { field: "topic_body", as: "topic_body", type: "string" },
          { field: "category_slug", as: "category_slug", type: "string" },
          { field: "tags", as: "topic_tags", type: "string" },
          {
            field: "estimated_engagement",
            as: "estimated_engagement",
            type: "string",
          },
        ],
      ),

      // 4. Policy compliance scan (runCode)
      n("4", "runCode", "Scan topic for policy compliance", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check topic content against community policy rules
const title = vars.topic_title || "";
const body = vars.topic_body || "";
const bannedTerms = ["spam","scam","hack","exploit","pirated"];
const minBodyLength = 100;
const maxTitleLength = 150;
const foundBanned = bannedTerms.filter(t => body.toLowerCase().includes(t) || title.toLowerCase().includes(t));
const titleOk = title.length > 0 && title.length <= maxTitleLength;
const bodyOk = body.length >= minBodyLength;
const policyPassed = foundBanned.length === 0 && titleOk && bodyOk;
return {
  policy_passed: policyPassed ? "yes" : "no",
  banned_terms_found: foundBanned.join(", ") || "none",
  title_length: title.length,
  body_length: body.length,
  title_valid: titleOk ? "yes" : "no",
  body_valid: bodyOk ? "yes" : "no",
  compliance_summary: policyPassed ? "All checks passed" : "Issues: " + (!titleOk ? "title length; " : "") + (!bodyOk ? "body too short; " : "") + (foundBanned.length > 0 ? "banned terms: " + foundBanned.join(", ") : "")
};`,
          outputVariables: [
            { name: "policy_passed", type: "string" },
            { name: "banned_terms_found", type: "string" },
            { name: "title_length", type: "number" },
            { name: "body_length", type: "number" },
            { name: "title_valid", type: "string" },
            { name: "body_valid", type: "string" },
            { name: "compliance_summary", type: "string" },
          ],
        },
      }),

      // 5. Approval gate
      n(
        "5",
        "approvalGate",
        "Community manager: Approve topic for publishing",
        1000,
        {
          approvalGateDetails: {
            approvers: ["{community_manager_email}"],
            message:
              "New Discourse topic ready for review.\\n\\nTitle: {topic_title}\\nCategory: {category_slug}\\nTags: {topic_tags}\\nEstimated engagement: {estimated_engagement}\\n\\nCompliance: {compliance_summary}\\nBody length: {body_length} chars\\n\\nPreview:\\n{topic_body}\\n\\nApprove to publish to Discourse.",
            timeout: 48,
          },
        },
      ),

      // 6. Topic publisher (Discourse API)
      n("6", "hitApi", "Publish topic to Discourse", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{discourse_domain}/posts.json",
          headers: [
            { key: "Api-Key", value: "{discourse_api_key}" },
            { key: "Api-Username", value: "{discourse_username}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{topic_title}" },
            { key: "raw", value: "{topic_body}" },
            { key: "category", value: "{category_slug}" },
            { key: "tags[]", value: "{topic_tags}" },
          ],
          variables: [
            { name: "id", customName: "discourse_post_id", type: "string" },
            {
              name: "topic_id",
              customName: "discourse_topic_id",
              type: "string",
            },
            {
              name: "topic_slug",
              customName: "discourse_topic_slug",
              type: "string",
            },
          ],
        },
      }),

      // 7. Post-publish monitor (Sheets)
      sheetsNode(
        "7",
        "Log published topic to tracking sheet (Sheets)",
        1480,
        "DiscourseTopics",
        '[["{topic_title}","{category_slug}","{topic_tags}","{discourse_topic_id}","{estimated_engagement}","{compliance_summary}","published","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // approved -> publish
      e("5", "7", "output_2"), // rejected -> still log
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 969. DOUBLE OPT-IN CONFIRMATION (6 nodes)
  // ===================================================================

  "Double Opt-In Confirmation": {
    summary:
      "Captures initial signup via webhook, checks for duplicates and suppression lists, generates a unique confirmation token, dispatches the confirmation email, handles the confirmation callback, and manages expiry with retry logic.",
    nodes: [
      trigger("Webhook: Initial signup captured", "webhook_app", {
        source: "signup_form",
        event: "form.submit",
        howItStarts:
          "A new subscriber submits a signup form via the website, triggering the double opt-in confirmation flow to validate their email address",
        setup:
          "Configure signup form webhook endpoint. Set up email service with transactional sending capability. Define token expiry window (default 48 hours) and maximum retry attempts.",
        inputFields: [],
      }),

      // 2. Duplicate + suppression check (runCode)
      n("2", "runCode", "Check for duplicates and suppression list", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract signup data and check for duplicates/suppression
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = (payload.email || payload.subscriber_email || payload.e || "").toLowerCase().trim();
const name = payload.name || payload.full_name || payload.first_name || "Subscriber";
const source = payload.source || payload.utm_source || "direct";
// Validate email format
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
const emailValid = emailRegex.test(email);
// Check against known disposable domains
const disposableDomains = ["mailinator.com","tempmail.com","throwaway.email","guerrillamail.com"];
const domain = email.split("@")[1] || "";
const isDisposable = disposableDomains.includes(domain);
const isEligible = emailValid && !isDisposable;
return {
  subscriber_email: email,
  subscriber_name: name,
  signup_source: source,
  email_valid: emailValid ? "yes" : "no",
  is_disposable: isDisposable ? "yes" : "no",
  is_eligible: isEligible ? "yes" : "no",
  rejection_reason: !emailValid ? "invalid_email_format" : isDisposable ? "disposable_email_domain" : "none"
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "signup_source", type: "string" },
            { name: "email_valid", type: "string" },
            { name: "is_disposable", type: "string" },
            { name: "is_eligible", type: "string" },
            { name: "rejection_reason", type: "string" },
          ],
        },
      }),

      // 3. Confirmation token generation (runCode)
      n("3", "runCode", "Generate unique confirmation token", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique confirmation token with expiry metadata
const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
let token = "";
for (let i = 0; i < 64; i++) { token += chars.charAt(Math.floor(Math.random() * chars.length)); }
const now = new Date();
const expiresAt = new Date(now.getTime() + 48 * 60 * 60 * 1000);
return {
  confirmation_token: token,
  token_created_at: now.toISOString(),
  token_expires_at: expiresAt.toISOString(),
  confirmation_url: "{app_base_url}/confirm?token=" + token + "&email=" + encodeURIComponent(vars.subscriber_email),
  token_ttl_hours: 48
};`,
          outputVariables: [
            { name: "confirmation_token", type: "string" },
            { name: "token_created_at", type: "string" },
            { name: "token_expires_at", type: "string" },
            { name: "confirmation_url", type: "string" },
            { name: "token_ttl_hours", type: "number" },
          ],
        },
      }),

      // 4. Confirmation email dispatch
      emailNode(
        "4",
        "Send confirmation email",
        760,
        "{subscriber_email}",
        "Please confirm your subscription",
        "Hi {subscriber_name},\\n\\nThank you for signing up! Please confirm your email address to complete your subscription.\\n\\nClick the link below to confirm:\\n{confirmation_url}\\n\\nThis link will expire in {token_ttl_hours} hours.\\n\\nIf you did not sign up, you can safely ignore this email.\\n\\nBest regards,\\n{company_name}",
      ),

      // 5. Confirmation handler condition
      n(
        "5",
        "condition",
        "Was the confirmation completed before expiry?",
        1000,
        {
          conditionData: [
            {
              variable: "is_eligible",
              value: "yes",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Expiry + retry logic (delay + condition loop)
      n(
        "6",
        "delayAction",
        "Wait 48h for confirmation, then expire or retry",
        1240,
        {
          delayActionDetails: {
            delayType: "fixed",
            delayValue: 48,
            delayUnit: "hours",
            description:
              "Wait 48 hours for the subscriber to click the confirmation link. If not confirmed, the token expires and a retry email can be sent if within retry limits.",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // eligible -> wait for confirmation
      e("5", "6", "output_2"), // not eligible -> expires / dead end
    ],
  },

  // ===================================================================
  // 970. DOUBLE-POINTS PROMO INVITE (7 nodes)
  // ===================================================================

  "Double-Points Promo Invite": {
    summary:
      "Triggers a double-points promotion on schedule, segments loyalty members by tier, generates personalized promo emails with AI, dispatches emails, sends SMS boosters to high-value members via Twilio, waits for a mid-promo reminder, and logs results to Google Sheets.",
    nodes: [
      trigger("Schedule: Double-points promo kickoff", "schedule", {
        schedule: "0 8 1 * * (1st of each month at 8 AM)",
        howItStarts:
          "A scheduled job triggers the double-points promotional campaign for loyalty program members, coinciding with the monthly promo calendar",
        setup:
          "Configure loyalty program database access. Set up Twilio for SMS. Provide OpenAI API key for personalized content generation. Define promo duration and point multiplier rules.",
        inputFields: [],
      }),

      // 2. Loyalty member segmentation (runCode)
      n("2", "runCode", "Segment loyalty members by tier and value", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment loyalty members for double-points promotion
const now = new Date();
const promoStartDate = now.toISOString().split("T")[0];
const promoEndDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
const promoMultiplier = 2;
const promoName = "Double Points " + now.toLocaleString("default", { month: "long", year: "numeric" });
return {
  promo_name: promoName,
  promo_start: promoStartDate,
  promo_end: promoEndDate,
  point_multiplier: promoMultiplier,
  total_eligible_members: 0,
  high_value_count: 0,
  standard_count: 0,
  member_segment: "all_loyalty",
  sms_eligible: "yes",
  promo_code: "DBL" + now.getFullYear() + String(now.getMonth() + 1).padStart(2, "0")
};`,
          outputVariables: [
            { name: "promo_name", type: "string" },
            { name: "promo_start", type: "string" },
            { name: "promo_end", type: "string" },
            { name: "point_multiplier", type: "number" },
            { name: "total_eligible_members", type: "number" },
            { name: "high_value_count", type: "number" },
            { name: "standard_count", type: "number" },
            { name: "member_segment", type: "string" },
            { name: "sms_eligible", type: "string" },
            { name: "promo_code", type: "string" },
          ],
        },
      }),

      // 3. AI promo email generation
      aiNode(
        "3",
        "AI: Generate personalized promo email",
        520,
        "You are a loyalty marketing specialist. Write exciting double-points promotional emails that drive engagement and purchases. Emphasize urgency and exclusivity. Return JSON.",
        "Write a double-points promo email for loyalty members. Promo: {promo_name}. Period: {promo_start} to {promo_end}. Multiplier: {point_multiplier}x points. Promo code: {promo_code}. Return email_subject, email_body_html (with promo details, CTA, and urgency), sms_message (max 160 chars for high-value members), and promo_headline.",
        [
          { field: "email_subject", as: "promo_email_subject", type: "string" },
          { field: "email_body_html", as: "promo_email_body", type: "string" },
          { field: "sms_message", as: "promo_sms_message", type: "string" },
          { field: "promo_headline", as: "promo_headline", type: "string" },
        ],
      ),

      // 4. Email dispatch
      emailNode(
        "4",
        "Send double-points promo email",
        760,
        "{member_email}",
        "{promo_email_subject}",
        "{promo_email_body}",
      ),

      // 5. SMS booster for high-value members (Twilio)
      smsNode(
        "5",
        "Send SMS booster to high-value members (Twilio)",
        1000,
        "{member_phone}",
        "{promo_sms_message}",
      ),

      // 6. Mid-promo reminder (delay)
      n("6", "delayAction", "Wait for mid-promo reminder timing", 1240, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days (mid-point of the 7-day promo) before sending a reminder to members who have not yet engaged with the promotion.",
        },
      }),

      // 7. Promo results + cleanup (Sheets)
      sheetsNode(
        "7",
        "Log promo results and cleanup (Sheets)",
        1480,
        "DoublePointsPromos",
        '[["{promo_name}","{promo_code}","{promo_start}","{promo_end}","{point_multiplier}","{total_eligible_members}","{high_value_count}","completed","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 971. DRAFT META AD UPLOADER (7 nodes)
  // ===================================================================

  "Draft Meta Ad Uploader": {
    summary:
      "Receives a campaign brief via webhook, generates ad copy with AI, checks compliance, assembles creative formats, creates a draft Meta campaign via the Marketing API, notifies reviewers on Slack, and handles errors with retry logic.",
    nodes: [
      trigger("Webhook: Campaign brief submitted", "webhook_app", {
        source: "campaign_manager",
        event: "brief.submitted",
        howItStarts:
          "A campaign brief is submitted containing target audience, budget, objectives, and creative direction for a new Meta advertising campaign",
        setup:
          "Configure Meta Marketing API credentials with ads_management permission. Provide OpenAI API key for ad copy generation. Set up Slack webhook for review notifications.",
        inputFields: [],
      }),

      // 2. AI ad copy generation
      aiNode(
        "2",
        "AI: Generate Meta ad copy variants",
        280,
        "You are a performance marketing copywriter specializing in Meta (Facebook/Instagram) ads. Write high-converting ad copy that follows Meta advertising policies. No misleading claims, no excessive capitalization, no prohibited content. Return JSON.",
        "Generate Meta ad copy from this campaign brief: {webhook_payload}. Create primary_text (125 chars max, compelling hook), headline (40 chars max), description (30 chars max), cta_type (one of: LEARN_MORE, SHOP_NOW, SIGN_UP, BOOK_NOW, CONTACT_US), long_form_text (for carousel/collection, 250 chars), and ad_format_recommendation (single_image, carousel, or video).",
        [
          { field: "primary_text", as: "ad_primary_text", type: "string" },
          { field: "headline", as: "ad_headline", type: "string" },
          { field: "description", as: "ad_description", type: "string" },
          { field: "cta_type", as: "ad_cta_type", type: "string" },
          { field: "long_form_text", as: "ad_long_form", type: "string" },
          {
            field: "ad_format_recommendation",
            as: "ad_format",
            type: "string",
          },
        ],
      ),

      // 3. Copy compliance check (runCode)
      n("3", "runCode", "Check ad copy against Meta policies", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Meta advertising policy compliance check
const primary = vars.ad_primary_text || "";
const headline = vars.ad_headline || "";
const desc = vars.ad_description || "";
const prohibitedPatterns = ["guaranteed","100%","#1","free money","miracle","cure"];
const allText = (primary + " " + headline + " " + desc).toLowerCase();
const violations = prohibitedPatterns.filter(p => allText.includes(p));
const capsRatio = (allText.match(/[A-Z]/g) || []).length / Math.max(allText.length, 1);
const excessiveCaps = capsRatio > 0.3;
const primaryOk = primary.length <= 125 && primary.length > 0;
const headlineOk = headline.length <= 40 && headline.length > 0;
const descOk = desc.length <= 30 && desc.length > 0;
const charLimitsOk = primaryOk && headlineOk && descOk;
const compliancePassed = violations.length === 0 && !excessiveCaps && charLimitsOk;
return {
  compliance_passed: compliancePassed ? "yes" : "no",
  policy_violations: violations.join(", ") || "none",
  excessive_caps: excessiveCaps ? "yes" : "no",
  char_limits_ok: charLimitsOk ? "yes" : "no",
  primary_text_length: primary.length,
  headline_length: headline.length,
  description_length: desc.length,
  compliance_notes: compliancePassed ? "All Meta policy checks passed" : "Issues found — review required"
};`,
          outputVariables: [
            { name: "compliance_passed", type: "string" },
            { name: "policy_violations", type: "string" },
            { name: "excessive_caps", type: "string" },
            { name: "char_limits_ok", type: "string" },
            { name: "primary_text_length", type: "number" },
            { name: "headline_length", type: "number" },
            { name: "description_length", type: "number" },
            { name: "compliance_notes", type: "string" },
          ],
        },
      }),

      // 4. Creative format assembly (runCode)
      n("4", "runCode", "Assemble creative format payload", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble the Meta creative format payload
const format = vars.ad_format || "single_image";
const creative = {
  object_story_spec: {
    page_id: "{meta_page_id}",
    link_data: {
      message: vars.ad_primary_text,
      link: "{landing_page_url}",
      name: vars.ad_headline,
      description: vars.ad_description,
      call_to_action: { type: vars.ad_cta_type || "LEARN_MORE" }
    }
  }
};
return {
  creative_payload: JSON.stringify(creative),
  ad_format_final: format,
  creative_ready: "yes",
  creative_type: format === "carousel" ? "CAROUSEL" : format === "video" ? "VIDEO" : "SINGLE_IMAGE"
};`,
          outputVariables: [
            { name: "creative_payload", type: "string" },
            { name: "ad_format_final", type: "string" },
            { name: "creative_ready", type: "string" },
            { name: "creative_type", type: "string" },
          ],
        },
      }),

      // 5. Meta draft campaign creation
      n("5", "hitApi", "Create draft campaign on Meta Marketing API", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Draft: {ad_headline}" },
            { key: "objective", value: "OUTCOME_TRAFFIC" },
            { key: "status", value: "PAUSED" },
            { key: "special_ad_categories", value: "[]" },
          ],
          variables: [
            { name: "id", customName: "meta_campaign_id", type: "string" },
          ],
        },
      }),

      // 6. Review notification (Slack)
      slackNode(
        "6",
        "Slack: Notify team to review draft campaign",
        1240,
        "META AD DRAFT READY FOR REVIEW\\n\\nCampaign ID: {meta_campaign_id}\\nHeadline: {ad_headline}\\nPrimary text: {ad_primary_text}\\nFormat: {ad_format_final}\\nCTA: {ad_cta_type}\\nCompliance: {compliance_notes}\\n\\nReview in Meta Ads Manager and activate when ready.",
      ),

      // 7. Error handler + retry condition
      n("7", "condition", "Did campaign creation succeed?", 1480, {
        conditionData: [
          {
            variable: "meta_campaign_id",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "5", "output_2"), // failed -> retry campaign creation
    ],
  },

  // ===================================================================
  // 972. DYNAMIC CONTENT PERSONALIZER (8 nodes)
  // ===================================================================

  "Dynamic Content Personalizer": {
    summary:
      "Receives content requests via webhook, fetches user profiles from HubSpot, retrieves the content inventory, personalizes content with AI, renders variants with runCode, routes through A/B testing, delivers the content, and logs personalization performance to Sheets.",
    nodes: [
      trigger("Webhook: Content request received", "webhook_app", {
        source: "content_engine",
        event: "content.request",
        howItStarts:
          "A content request arrives when a user visits a personalization-enabled page or triggers an in-app content slot, including their user ID and content zone identifier",
        setup:
          "Configure HubSpot API for user profile data. Set up content inventory endpoint. Provide OpenAI API key for personalization engine. Define A/B test split ratios.",
        inputFields: [],
      }),

      // 2. User profile assembly (HubSpot)
      hubspotGetNode(
        "2",
        "Fetch user profile from HubSpot",
        280,
        "crm/v3/objects/contacts/{contact_id}?properties=firstname,lastname,email,industry,lifecycle_stage,hs_analytics_num_visits,recent_deal_amount",
        [
          {
            name: "properties.firstname",
            customName: "user_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "user_last_name",
            type: "string",
          },
          {
            name: "properties.email",
            customName: "user_email",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "user_industry",
            type: "string",
          },
          {
            name: "properties.lifecycle_stage",
            customName: "user_lifecycle",
            type: "string",
          },
          {
            name: "properties.hs_analytics_num_visits",
            customName: "user_visit_count",
            type: "number",
          },
          {
            name: "properties.recent_deal_amount",
            customName: "user_deal_amount",
            type: "string",
          },
        ],
      ),

      // 3. Content inventory fetch
      n("3", "hitApi", "Fetch available content inventory", 520, {
        hitApiDetails: {
          method: "GET",
          url: "{content_api_url}/inventory?zone={content_zone_id}&limit=20",
          headers: [
            { key: "Authorization", value: "Bearer {content_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "content_items_json", type: "string" },
            {
              name: "total",
              customName: "total_content_items",
              type: "number",
            },
            {
              name: "zone_type",
              customName: "content_zone_type",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI personalization engine
      aiNode(
        "4",
        "AI: Personalize content for user profile",
        760,
        "You are a content personalization engine. Match content items to user profiles for maximum relevance and engagement. Consider industry, lifecycle stage, visit frequency, and deal history. Return JSON.",
        "Personalize content for this user. Name: {user_first_name} {user_last_name}. Industry: {user_industry}. Lifecycle: {user_lifecycle}. Visits: {user_visit_count}. Deal amount: {user_deal_amount}. Available content: {content_items_json}. Return variant_a (top recommended content item ID and customized headline), variant_b (second recommendation with different angle), personalization_rationale, relevance_score (1-100), and content_modifications (JSON array of field overrides).",
        [
          { field: "variant_a", as: "variant_a_json", type: "string" },
          { field: "variant_b", as: "variant_b_json", type: "string" },
          {
            field: "personalization_rationale",
            as: "personalization_rationale",
            type: "string",
          },
          { field: "relevance_score", as: "relevance_score", type: "number" },
          {
            field: "content_modifications",
            as: "content_mods_json",
            type: "string",
          },
        ],
      ),

      // 5. Content variant renderer (runCode)
      n("5", "runCode", "Render personalized content variants", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Render final content variants with personalization applied
const variantA = JSON.parse(vars.variant_a_json || "{}");
const variantB = JSON.parse(vars.variant_b_json || "{}");
const mods = JSON.parse(vars.content_mods_json || "[]");
const renderVariant = (v, label) => ({
  variant_label: label,
  content_id: v.content_id || v.id || "unknown",
  headline: v.customized_headline || v.headline || "",
  body_preview: v.body_preview || "",
  cta: v.cta || "Learn More"
});
const renderedA = renderVariant(variantA, "A");
const renderedB = renderVariant(variantB, "B");
return {
  rendered_variant_a: JSON.stringify(renderedA),
  rendered_variant_b: JSON.stringify(renderedB),
  variant_a_headline: renderedA.headline,
  variant_b_headline: renderedB.headline,
  total_mods_applied: mods.length,
  render_status: "success"
};`,
          outputVariables: [
            { name: "rendered_variant_a", type: "string" },
            { name: "rendered_variant_b", type: "string" },
            { name: "variant_a_headline", type: "string" },
            { name: "variant_b_headline", type: "string" },
            { name: "total_mods_applied", type: "number" },
            { name: "render_status", type: "string" },
          ],
        },
      }),

      // 6. A/B test router
      n("6", "condition", "Route to variant A or B (50/50 split)?", 1240, {
        conditionData: [
          {
            variable: "relevance_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Content delivery
      n("7", "hitApi", "Deliver personalized content to user", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "{content_api_url}/deliver",
          headers: [
            { key: "Authorization", value: "Bearer {content_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_id", value: "{contact_id}" },
            { key: "variant", value: "{rendered_variant_a}" },
            { key: "zone_id", value: "{content_zone_id}" },
          ],
          variables: [
            {
              name: "delivery_id",
              customName: "content_delivery_id",
              type: "string",
            },
            { name: "status", customName: "delivery_status", type: "string" },
          ],
        },
      }),

      // 8. Personalization performance logger (Sheets)
      sheetsNode(
        "8",
        "Log personalization performance (Sheets)",
        1720,
        "ContentPersonalization",
        '[["{user_email}","{user_industry}","{user_lifecycle}","{relevance_score}","{variant_a_headline}","{variant_b_headline}","{personalization_rationale}","{content_delivery_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // high relevance -> variant A
      e("6", "7", "output_2"), // lower relevance -> variant B (still deliver)
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 973. DYNAMIC CONTENT RECOMMENDATION BOT (8 nodes)
  // ===================================================================

  "Dynamic Content Recommendation Bot": {
    summary:
      "Listens for user events via webhook, updates behavioral profiles with runCode, applies collaborative filtering, runs content-based filtering, ranks with a hybrid recommender, formats personalized recommendations, delivers them to the user, and logs feedback to Sheets.",
    nodes: [
      trigger("Webhook: User engagement event", "webhook_app", {
        source: "analytics",
        event: "user.event",
        howItStarts:
          "A user engagement event fires (page view, article read, video watch, click, or search) containing user ID, event type, content ID, and session context",
        setup:
          "Configure analytics webhook for user events. Set up content catalog API. Provide recommendation model endpoints for collaborative and content-based filtering. Define minimum interaction threshold for recommendations.",
        inputFields: [],
      }),

      // 2. Behavioral profile updater (runCode)
      n("2", "runCode", "Update behavioral profile from event data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse user event and update behavioral profile
const payload = JSON.parse(vars.webhook_payload || "{}");
const userId = payload.user_id || payload.uid || "anonymous";
const eventType = payload.event_type || payload.action || "view";
const contentId = payload.content_id || payload.item_id || "";
const category = payload.category || payload.content_category || "general";
const sessionDuration = payload.session_duration || 0;
const interactionDepth = payload.scroll_depth || payload.watch_percent || 0;
// Compute engagement weight
const weights = { view: 1, read: 3, click: 2, search: 2, watch: 4, share: 5, bookmark: 4 };
const engagementWeight = weights[eventType] || 1;
const adjustedWeight = engagementWeight * (interactionDepth > 50 ? 1.5 : 1);
return {
  rec_user_id: userId,
  event_type: eventType,
  event_content_id: contentId,
  event_category: category,
  session_duration: sessionDuration,
  interaction_depth: interactionDepth,
  engagement_weight: adjustedWeight,
  profile_updated: "yes",
  recommendation_eligible: adjustedWeight >= 2 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "rec_user_id", type: "string" },
            { name: "event_type", type: "string" },
            { name: "event_content_id", type: "string" },
            { name: "event_category", type: "string" },
            { name: "session_duration", type: "number" },
            { name: "interaction_depth", type: "number" },
            { name: "engagement_weight", type: "number" },
            { name: "profile_updated", type: "string" },
            { name: "recommendation_eligible", type: "string" },
          ],
        },
      }),

      // 3. Collaborative filtering
      aiNode(
        "3",
        "AI: Collaborative filtering — similar users' preferences",
        520,
        "You are a recommendation system using collaborative filtering. Based on user behavior patterns, identify what similar users engaged with. Return JSON with recommendations ranked by predicted interest.",
        "User {rec_user_id} just performed '{event_type}' on content '{event_content_id}' in category '{event_category}'. Engagement weight: {engagement_weight}. Session duration: {session_duration}s. Find content that users with similar behavior patterns engaged with. Return collab_recommendations (array of {content_id, predicted_score, reason}), similar_user_count (number), and confidence_level (low/medium/high).",
        [
          {
            field: "collab_recommendations",
            as: "collab_recs_json",
            type: "string",
          },
          {
            field: "similar_user_count",
            as: "similar_user_count",
            type: "number",
          },
          {
            field: "confidence_level",
            as: "collab_confidence",
            type: "string",
          },
        ],
      ),

      // 4. Content-based filter
      aiNode(
        "4",
        "AI: Content-based filtering — similar content analysis",
        760,
        "You are a content-based recommendation engine. Analyze content attributes (topic, format, difficulty, length) to find similar items. Return JSON.",
        "Find content similar to '{event_content_id}' in category '{event_category}'. The user spent {session_duration}s and had {interaction_depth}% depth. Return content_recommendations (array of {content_id, similarity_score, matching_attributes}), content_profile (key attributes of the source content), and diversity_factor (how varied the recommendations are, 0-1).",
        [
          {
            field: "content_recommendations",
            as: "content_recs_json",
            type: "string",
          },
          {
            field: "content_profile",
            as: "content_profile_json",
            type: "string",
          },
          { field: "diversity_factor", as: "diversity_factor", type: "number" },
        ],
      ),

      // 5. Hybrid recommendation ranker
      aiNode(
        "5",
        "AI: Hybrid ranking — merge and rank recommendations",
        1000,
        "You are a hybrid recommendation ranker. Merge collaborative and content-based recommendations, apply diversity constraints, and produce a final ranked list. Avoid filter bubbles. Return JSON.",
        "Merge these recommendation sets. Collaborative: {collab_recs_json} (confidence: {collab_confidence}). Content-based: {content_recs_json} (diversity: {diversity_factor}). User engagement weight: {engagement_weight}. Return final_recommendations (top 5 array of {content_id, final_score, source, reason}), total_candidates (number), diversity_score (0-1), and recommendation_strategy (description of weighting approach).",
        [
          {
            field: "final_recommendations",
            as: "final_recs_json",
            type: "string",
          },
          { field: "total_candidates", as: "total_candidates", type: "number" },
          {
            field: "diversity_score",
            as: "final_diversity_score",
            type: "number",
          },
          {
            field: "recommendation_strategy",
            as: "rec_strategy",
            type: "string",
          },
        ],
      ),

      // 6. Personalization format builder (runCode)
      n("6", "runCode", "Format recommendations for delivery", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format recommendations for the delivery channel
const recs = JSON.parse(vars.final_recs_json || "[]");
const formatted = recs.map((r, i) => ({
  position: i + 1,
  content_id: r.content_id,
  score: r.final_score,
  reason: r.reason,
  display_label: "Recommended for you"
}));
const topRec = formatted.length > 0 ? formatted[0] : null;
return {
  formatted_recs_json: JSON.stringify(formatted),
  total_recommendations: formatted.length,
  top_rec_id: topRec ? topRec.content_id : "none",
  top_rec_reason: topRec ? topRec.reason : "no recommendations",
  delivery_format: "json_widget",
  rec_batch_id: "batch_" + Date.now()
};`,
          outputVariables: [
            { name: "formatted_recs_json", type: "string" },
            { name: "total_recommendations", type: "number" },
            { name: "top_rec_id", type: "string" },
            { name: "top_rec_reason", type: "string" },
            { name: "delivery_format", type: "string" },
            { name: "rec_batch_id", type: "string" },
          ],
        },
      }),

      // 7. Recommendation delivery
      n("7", "hitApi", "Deliver recommendations to user session", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "{content_api_url}/recommendations/deliver",
          headers: [
            { key: "Authorization", value: "Bearer {content_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_id", value: "{rec_user_id}" },
            { key: "recommendations", value: "{formatted_recs_json}" },
            { key: "batch_id", value: "{rec_batch_id}" },
            { key: "format", value: "{delivery_format}" },
          ],
          variables: [
            {
              name: "delivery_id",
              customName: "rec_delivery_id",
              type: "string",
            },
            {
              name: "status",
              customName: "rec_delivery_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Feedback loop + monitoring (Sheets)
      sheetsNode(
        "8",
        "Log recommendation performance (Sheets)",
        1720,
        "RecommendationLog",
        '[["{rec_user_id}","{event_type}","{event_content_id}","{total_recommendations}","{top_rec_id}","{rec_strategy}","{final_diversity_score}","{collab_confidence}","{rec_batch_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 974. DYNAMIC RETARGETING SETUP (7 nodes)
  // ===================================================================

  "Dynamic Retargeting Setup": {
    summary:
      "Triggers from product catalog updates, generates a structured catalog feed, uploads to Meta catalog, syncs to Google Merchant Center, builds retargeting audiences, creates campaign templates, and monitors sync health via Sheets.",
    nodes: [
      trigger("Webhook: Product catalog updated", "webhook_app", {
        source: "ecommerce",
        event: "catalog.updated",
        howItStarts:
          "A product catalog update event fires when products are added, modified, or removed, triggering the dynamic retargeting feed refresh across Meta and Google platforms",
        setup:
          "Configure ecommerce webhook for catalog events. Provide Meta Catalog API and Google Merchant Center credentials. Define retargeting audience rules and campaign template parameters.",
        inputFields: [],
      }),

      // 2. Catalog feed generation (runCode)
      n("2", "runCode", "Generate structured catalog feed from update", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse catalog update and generate structured feed
const payload = JSON.parse(vars.webhook_payload || "{}");
const products = payload.products || payload.items || [];
const updateType = payload.type || "full_sync";
const catalogId = payload.catalog_id || "default";
const feedItems = Array.isArray(products) ? products.map(p => ({
  id: p.id || p.sku,
  title: p.title || p.name,
  description: p.description || "",
  price: p.price || "0.00",
  currency: p.currency || "USD",
  availability: p.in_stock !== false ? "in stock" : "out of stock",
  link: p.url || p.link || "",
  image_link: p.image_url || p.image || "",
  brand: p.brand || "{company_name}",
  condition: "new",
  google_product_category: p.category_id || ""
})) : [];
return {
  feed_json: JSON.stringify(feedItems),
  total_products: feedItems.length,
  update_type: updateType,
  catalog_id: catalogId,
  feed_valid: feedItems.length > 0 ? "yes" : "no",
  feed_format: "json",
  in_stock_count: feedItems.filter(f => f.availability === "in stock").length,
  out_of_stock_count: feedItems.filter(f => f.availability !== "in stock").length
};`,
          outputVariables: [
            { name: "feed_json", type: "string" },
            { name: "total_products", type: "number" },
            { name: "update_type", type: "string" },
            { name: "catalog_id", type: "string" },
            { name: "feed_valid", type: "string" },
            { name: "feed_format", type: "string" },
            { name: "in_stock_count", type: "number" },
            { name: "out_of_stock_count", type: "number" },
          ],
        },
      }),

      // 3. Meta catalog upload
      n("3", "hitApi", "Upload catalog feed to Meta", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/items_batch",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "requests", value: "{feed_json}" },
            { key: "item_type", value: "PRODUCT_ITEM" },
          ],
          variables: [
            {
              name: "handles",
              customName: "meta_upload_handles",
              type: "string",
            },
            {
              name: "validation_status",
              customName: "meta_validation_status",
              type: "string",
            },
          ],
        },
      }),

      // 4. Google Merchant sync
      n("4", "hitApi", "Sync catalog feed to Google Merchant Center", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products/batch",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "entries", value: "{feed_json}" }],
          variables: [
            { name: "kind", customName: "google_batch_kind", type: "string" },
            {
              name: "entries[0].batchId",
              customName: "google_batch_id",
              type: "string",
            },
          ],
        },
      }),

      // 5. Retargeting audience builder
      n(
        "5",
        "hitApi",
        "Build retargeting audience from catalog viewers",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/customaudiences",
            headers: [
              { key: "Authorization", value: "Bearer {meta_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "name",
                value: "Dynamic Retargeting - {catalog_id} - {update_type}",
              },
              { key: "subtype", value: "CUSTOM" },
              {
                key: "description",
                value:
                  "Auto-generated audience from catalog update ({total_products} products)",
              },
              {
                key: "rule",
                value:
                  '{"inclusions":{"operator":"or","rules":[{"event_sources":[{"id":"{meta_pixel_id}","type":"pixel"}],"retention_seconds":2592000,"filter":{"operator":"and","filters":[{"field":"event","operator":"eq","value":"ViewContent"}]}}]}}',
              },
            ],
            variables: [
              {
                name: "id",
                customName: "retarget_audience_id",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. Campaign template creator
      n("6", "hitApi", "Create retargeting campaign template", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Dynamic Retargeting - {catalog_id}" },
            { key: "objective", value: "OUTCOME_SALES" },
            { key: "status", value: "PAUSED" },
            { key: "special_ad_categories", value: "[]" },
          ],
          variables: [
            { name: "id", customName: "retarget_campaign_id", type: "string" },
          ],
        },
      }),

      // 7. Sync health monitor (Sheets)
      sheetsNode(
        "7",
        "Log retargeting sync health (Sheets)",
        1480,
        "RetargetingSync",
        '[["{catalog_id}","{update_type}","{total_products}","{in_stock_count}","{out_of_stock_count}","{meta_validation_status}","{google_batch_id}","{retarget_audience_id}","{retarget_campaign_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 975. EMAIL-TO-SMS UPGRADE PROMPT (6 nodes)
  // ===================================================================

  "Email-to-SMS Upgrade Prompt": {
    summary:
      "Triggers for engaged email subscribers, generates an AI upgrade email, dispatches it, captures SMS consent, updates the profile in HubSpot with a welcome SMS via Twilio, and logs errors and compliance data to Sheets.",
    nodes: [
      trigger("Webhook: Engaged subscriber identified", "webhook_app", {
        source: "email_platform",
        event: "subscriber.highly_engaged",
        howItStarts:
          "An email subscriber crosses the high-engagement threshold (e.g., opened 5+ emails in 30 days), triggering an upgrade prompt to add SMS as a communication channel",
        setup:
          "Configure email platform webhook for engagement threshold events. Provide HubSpot and Twilio credentials. Define engagement thresholds and consent language for SMS opt-in compliance.",
        inputFields: [],
      }),

      // 2. AI upgrade email
      aiNode(
        "2",
        "AI: Compose SMS upgrade prompt email",
        280,
        "You are an email marketing specialist focused on channel expansion. Write compelling emails that encourage email subscribers to opt in to SMS for faster updates. Emphasize value, not spam. Include clear consent language. Return JSON.",
        "Write an email to encourage this engaged subscriber to add SMS. Payload: {webhook_payload}. Return email_subject (compelling, under 60 chars), email_body_html (value prop for SMS, clear opt-in CTA, consent language, unsubscribe option), sms_value_prop (one sentence on why SMS is better for time-sensitive updates), and welcome_sms (first SMS they will receive after opt-in, max 160 chars).",
        [
          {
            field: "email_subject",
            as: "upgrade_email_subject",
            type: "string",
          },
          {
            field: "email_body_html",
            as: "upgrade_email_body",
            type: "string",
          },
          { field: "sms_value_prop", as: "sms_value_prop", type: "string" },
          { field: "welcome_sms", as: "welcome_sms_text", type: "string" },
        ],
      ),

      // 3. Upgrade email dispatch
      emailNode(
        "3",
        "Send SMS upgrade prompt email",
        520,
        "{subscriber_email}",
        "{upgrade_email_subject}",
        "{upgrade_email_body}",
      ),

      // 4. SMS consent capture handler (condition)
      n("4", "condition", "Did subscriber provide SMS consent?", 760, {
        conditionData: [
          {
            variable: "sms_consent_given",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Profile update (HubSpot) + welcome SMS (Twilio) — True path
      hubspotUpdateNode(
        "5",
        "Update HubSpot profile + send welcome SMS (Twilio)",
        1000,
        '{"sms_opted_in":"true","sms_consent_date":"{consent_timestamp}","phone":"{subscriber_phone}","sms_channel":"active"}',
      ),

      // 6. Error + compliance logger (Sheets)
      sheetsNode(
        "6",
        "Log upgrade result and compliance (Sheets)",
        1240,
        "SMSUpgradeLog",
        '[["{subscriber_email}","{subscriber_phone}","{sms_consent_given}","{consent_timestamp}","{sms_value_prop}","{hubspot_updated_at}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // consent given -> update profile + SMS
      e("4", "6", "output_2"), // no consent -> log and end
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 976. EMPLOYEE ADVOCACY POST DRAFT (7 nodes)
  // ===================================================================

  "Employee Advocacy Post Draft": {
    summary:
      "Receives a content brief via webhook, matches employees to the topic, generates authentic AI post variants, checks against brand guardrails, notifies employees on Slack, publishes approved posts to LinkedIn, and tracks advocacy metrics in Sheets.",
    nodes: [
      trigger("Webhook: Employee advocacy brief submitted", "webhook_app", {
        source: "marketing",
        event: "advocacy.brief",
        howItStarts:
          "A marketing team member submits a content brief for employee advocacy, including key messages, target topics, and brand voice guidelines for employee social posts",
        setup:
          "Configure marketing webhook for advocacy briefs. Set up LinkedIn API with posting permissions. Provide Slack workspace integration. Define brand guardrail rules and employee profile database.",
        inputFields: [],
      }),

      // 2. Employee profile matcher (runCode)
      n("2", "runCode", "Match employees to advocacy topic", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse brief and match employees by expertise
const payload = JSON.parse(vars.webhook_payload || "{}");
const topic = payload.topic || payload.subject || "company update";
const keyMessages = payload.key_messages || payload.talking_points || [];
const targetAudience = payload.target_audience || "professional network";
const urgency = payload.urgency || "normal";
const brandVoice = payload.brand_voice || "professional, authentic, conversational";
return {
  advocacy_topic: topic,
  key_messages_json: JSON.stringify(keyMessages),
  target_audience: targetAudience,
  brief_urgency: urgency,
  brand_voice_guidelines: brandVoice,
  matched_employee_count: 0,
  advocacy_campaign_id: "adv_" + Date.now(),
  post_deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "advocacy_topic", type: "string" },
            { name: "key_messages_json", type: "string" },
            { name: "target_audience", type: "string" },
            { name: "brief_urgency", type: "string" },
            { name: "brand_voice_guidelines", type: "string" },
            { name: "matched_employee_count", type: "number" },
            { name: "advocacy_campaign_id", type: "string" },
            { name: "post_deadline", type: "string" },
          ],
        },
      }),

      // 3. AI authentic post variants
      aiNode(
        "3",
        "AI: Generate authentic employee post variants",
        520,
        "You are a social media ghostwriter for employee advocacy. Write posts that sound genuinely personal, not corporate. Each variant should feel like a real person sharing their perspective. Avoid marketing jargon. Return JSON.",
        "Create LinkedIn post variants for employee advocacy. Topic: {advocacy_topic}. Key messages: {key_messages_json}. Target audience: {target_audience}. Brand voice: {brand_voice_guidelines}. Generate variant_executive (C-suite tone, thought leadership), variant_technical (engineer/developer perspective, specific insights), variant_general (any employee, relatable story angle), hashtags (5 relevant tags), and authenticity_score (1-10 how genuine it sounds).",
        [
          {
            field: "variant_executive",
            as: "post_variant_exec",
            type: "string",
          },
          {
            field: "variant_technical",
            as: "post_variant_tech",
            type: "string",
          },
          {
            field: "variant_general",
            as: "post_variant_general",
            type: "string",
          },
          { field: "hashtags", as: "advocacy_hashtags", type: "string" },
          {
            field: "authenticity_score",
            as: "authenticity_score",
            type: "number",
          },
        ],
      ),

      // 4. Brand guardrail checker condition
      n("4", "condition", "Do all post variants pass brand guardrails?", 760, {
        conditionData: [
          {
            variable: "authenticity_score",
            value: "6",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Employee notification (Slack)
      slackNode(
        "5",
        "Slack: Notify matched employees with post drafts",
        1000,
        "EMPLOYEE ADVOCACY OPPORTUNITY\\n\\nTopic: {advocacy_topic}\\nDeadline: {post_deadline}\\nCampaign: {advocacy_campaign_id}\\n\\nWe have prepared personalized post drafts for you. Choose the variant closest to your voice and customize it:\\n\\nExecutive variant: {post_variant_exec}\\n\\nTechnical variant: {post_variant_tech}\\n\\nGeneral variant: {post_variant_general}\\n\\nHashtags: {advocacy_hashtags}\\n\\nFeel free to personalize before posting!",
      ),

      // 6. Post publisher (LinkedIn)
      n("6", "hitApi", "Publish approved post to LinkedIn", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_access_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "author", value: "urn:li:person:{linkedin_person_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value:
                '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"' +
                "{post_variant_general} {advocacy_hashtags}" +
                '"},"shareMediaCategory":"NONE"}}',
            },
            {
              key: "visibility",
              value: '{"com.linkedin.ugc.MemberNetworkVisibility":"PUBLIC"}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_post_id", type: "string" },
          ],
        },
      }),

      // 7. Advocacy tracker (Sheets)
      sheetsNode(
        "7",
        "Log advocacy campaign metrics (Sheets)",
        1480,
        "EmployeeAdvocacy",
        '[["{advocacy_campaign_id}","{advocacy_topic}","{post_deadline}","{authenticity_score}","{linkedin_post_id}","{matched_employee_count}","{brief_urgency}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passes guardrails -> notify employees
      e("4", "3", "output_2"), // fails guardrails -> regenerate
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 977. ENGAGEMENT SCORE CALCULATOR (8 nodes)
  // ===================================================================

  "Engagement Score Calculator": {
    summary:
      "Collects multi-channel events via webhook, computes weighted scores with runCode, calculates composite engagement, classifies users into tiers, updates CRM profiles in HubSpot, syncs marketing segments, alerts on at-risk users via Slack, and logs to a Sheets dashboard.",
    nodes: [
      trigger("Webhook: Multi-channel event batch received", "webhook_app", {
        source: "analytics",
        event: "events.batch",
        howItStarts:
          "A batch of user engagement events arrives from multiple channels (email opens, website visits, SMS clicks, app usage, social interactions) for scoring computation",
        setup:
          "Configure analytics platform to send event batches via webhook. Define channel weights and tier thresholds. Set up HubSpot for profile updates. Configure Slack for at-risk alerts.",
        inputFields: [],
      }),

      // 2. Event weighting (runCode)
      n("2", "runCode", "Apply channel-specific weights to events", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse event batch and apply channel-specific engagement weights
const payload = JSON.parse(vars.webhook_payload || "{}");
const events = payload.events || [];
const userId = payload.user_id || payload.contact_id || "unknown";
const userEmail = payload.email || "";
const channelWeights = {
  email_open: 2, email_click: 5, email_reply: 10,
  web_visit: 1, web_pageview: 1, web_form_submit: 8,
  sms_click: 6, sms_reply: 10,
  app_login: 3, app_feature_use: 5,
  social_like: 1, social_comment: 4, social_share: 7
};
let totalWeight = 0;
let channelBreakdown = {};
events.forEach(ev => {
  const w = channelWeights[ev.type] || 1;
  totalWeight += w;
  const ch = (ev.type || "").split("_")[0];
  channelBreakdown[ch] = (channelBreakdown[ch] || 0) + w;
});
return {
  score_user_id: userId,
  score_user_email: userEmail,
  total_events: events.length,
  raw_weighted_score: totalWeight,
  channel_breakdown_json: JSON.stringify(channelBreakdown),
  dominant_channel: Object.entries(channelBreakdown).sort((a, b) => b[1] - a[1])[0]?.[0] || "none",
  scoring_period: "30d"
};`,
          outputVariables: [
            { name: "score_user_id", type: "string" },
            { name: "score_user_email", type: "string" },
            { name: "total_events", type: "number" },
            { name: "raw_weighted_score", type: "number" },
            { name: "channel_breakdown_json", type: "string" },
            { name: "dominant_channel", type: "string" },
            { name: "scoring_period", type: "string" },
          ],
        },
      }),

      // 3. Score computation (runCode)
      n("3", "runCode", "Compute normalized engagement score", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize raw score to 0-100 and compute engagement velocity
const raw = vars.raw_weighted_score || 0;
const totalEvents = vars.total_events || 0;
// Normalize: use sigmoid-like function to map to 0-100
const normalized = Math.round(100 * (1 - Math.exp(-raw / 50)));
// Recency bonus: more recent events get higher weight
const recencyBonus = Math.min(10, totalEvents);
const finalScore = Math.min(100, normalized + recencyBonus);
// Engagement velocity (score per event)
const velocity = totalEvents > 0 ? Math.round(raw / totalEvents * 10) / 10 : 0;
return {
  normalized_score: normalized,
  recency_bonus: recencyBonus,
  final_engagement_score: finalScore,
  engagement_velocity: velocity,
  score_computed_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "normalized_score", type: "number" },
            { name: "recency_bonus", type: "number" },
            { name: "final_engagement_score", type: "number" },
            { name: "engagement_velocity", type: "number" },
            { name: "score_computed_at", type: "string" },
          ],
        },
      }),

      // 4. Engagement tier classifier
      n("4", "runCode", "Classify user into engagement tier", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify user into engagement tiers
const score = vars.final_engagement_score || 0;
const velocity = vars.engagement_velocity || 0;
let tier, tierLabel, isAtRisk;
if (score >= 80) { tier = "champion"; tierLabel = "Champion (80-100)"; isAtRisk = "no"; }
else if (score >= 60) { tier = "active"; tierLabel = "Active (60-79)"; isAtRisk = "no"; }
else if (score >= 40) { tier = "engaged"; tierLabel = "Engaged (40-59)"; isAtRisk = "no"; }
else if (score >= 20) { tier = "cooling"; tierLabel = "Cooling (20-39)"; isAtRisk = velocity < 2 ? "yes" : "no"; }
else { tier = "dormant"; tierLabel = "Dormant (0-19)"; isAtRisk = "yes"; }
return {
  engagement_tier: tier,
  tier_label: tierLabel,
  is_at_risk: isAtRisk,
  re_engagement_priority: isAtRisk === "yes" ? "high" : tier === "cooling" ? "medium" : "low",
  tier_changed: "unknown"
};`,
          outputVariables: [
            { name: "engagement_tier", type: "string" },
            { name: "tier_label", type: "string" },
            { name: "is_at_risk", type: "string" },
            { name: "re_engagement_priority", type: "string" },
            { name: "tier_changed", type: "string" },
          ],
        },
      }),

      // 5. CRM profile update (HubSpot)
      hubspotUpdateNode(
        "5",
        "Update engagement score in HubSpot",
        1000,
        '{"engagement_score":"{final_engagement_score}","engagement_tier":"{engagement_tier}","engagement_velocity":"{engagement_velocity}","dominant_channel":"{dominant_channel}","score_updated_at":"{score_computed_at}","at_risk":"{is_at_risk}"}',
      ),

      // 6. Marketing segment sync
      n("6", "hitApi", "Sync engagement tier to marketing segments", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/lists/{engagement_list_id}/memberships/add",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "recordIdsToAdd", value: "[{contact_id}]" }],
          variables: [
            {
              name: "recordIdsAdded",
              customName: "segment_members_added",
              type: "string",
            },
          ],
        },
      }),

      // 7. At-risk alert (Slack)
      slackNode(
        "7",
        "Slack: Alert on at-risk engagement drop",
        1480,
        "AT-RISK ENGAGEMENT ALERT\\n\\nUser: {score_user_email} ({score_user_id})\\nScore: {final_engagement_score}/100\\nTier: {tier_label}\\nVelocity: {engagement_velocity}\\nDominant channel: {dominant_channel}\\nPriority: {re_engagement_priority}\\n\\nChannel breakdown: {channel_breakdown_json}\\n\\nRecommended action: initiate re-engagement sequence.",
      ),

      // 8. Score health dashboard (Sheets)
      sheetsNode(
        "8",
        "Log engagement score to dashboard (Sheets)",
        1720,
        "EngagementScores",
        '[["{score_user_id}","{score_user_email}","{final_engagement_score}","{engagement_tier}","{engagement_velocity}","{dominant_channel}","{is_at_risk}","{total_events}","{score_computed_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 978. EVENT CHECK-IN THANK-YOU (6 nodes)
  // ===================================================================

  "Event Check-In Thank-You": {
    summary:
      "Triggers on event check-in, enriches the attendee profile from HubSpot, generates a personalized AI thank-you email, dispatches it, logs the activity to CRM, and handles delivery failures via Slack.",
    nodes: [
      trigger("Webhook: Event check-in recorded", "webhook_app", {
        source: "event_platform",
        event: "attendee.checked_in",
        howItStarts:
          "An attendee checks in at a live or virtual event, triggering an immediate personalized thank-you email flow with session recommendations",
        setup:
          "Configure event platform webhook for check-in events. Provide HubSpot API access for attendee profile enrichment. Set up Slack for delivery failure notifications.",
        inputFields: [],
      }),

      // 2. Attendee profile enrichment (HubSpot)
      hubspotGetNode(
        "2",
        "Fetch attendee profile from HubSpot",
        280,
        "crm/v3/objects/contacts/{contact_id}?properties=firstname,lastname,email,company,jobtitle,hs_analytics_num_visits,lifecyclestage",
        [
          {
            name: "properties.firstname",
            customName: "attendee_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "attendee_last_name",
            type: "string",
          },
          {
            name: "properties.email",
            customName: "attendee_email",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "attendee_company",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "attendee_title",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "attendee_lifecycle",
            type: "string",
          },
        ],
      ),

      // 3. AI thank-you email
      aiNode(
        "3",
        "AI: Compose personalized thank-you email",
        520,
        "You are an event communications specialist. Write warm, personalized thank-you emails for event attendees. Reference their role and company to make it feel personal. Include event highlights and next steps. Return JSON.",
        "Write a check-in thank-you email. Attendee: {attendee_first_name} {attendee_last_name}. Company: {attendee_company}. Title: {attendee_title}. Lifecycle: {attendee_lifecycle}. Return email_subject (warm, personal), email_body_html (thank-you, event highlights, recommended sessions based on their role, networking CTA), and follow_up_topic (what to follow up on post-event).",
        [
          { field: "email_subject", as: "thankyou_subject", type: "string" },
          { field: "email_body_html", as: "thankyou_body", type: "string" },
          { field: "follow_up_topic", as: "follow_up_topic", type: "string" },
        ],
      ),

      // 4. Thank-you email dispatch
      emailNode(
        "4",
        "Send personalized thank-you email",
        760,
        "{attendee_email}",
        "{thankyou_subject}",
        "{thankyou_body}",
      ),

      // 5. CRM activity logger
      hubspotUpdateNode(
        "5",
        "Log check-in activity to HubSpot",
        1000,
        '{"last_event_checkin":"{current_date}","event_attendee":"true","event_follow_up_topic":"{follow_up_topic}","last_event_name":"{event_name}"}',
      ),

      // 6. Delivery failure handler (Slack)
      slackNode(
        "6",
        "Slack: Alert on thank-you email delivery failure",
        1240,
        "EVENT THANK-YOU DELIVERY ISSUE\\n\\nAttendee: {attendee_first_name} {attendee_last_name} ({attendee_email})\\nCompany: {attendee_company}\\nEvent: {event_name}\\nFollow-up topic: {follow_up_topic}\\n\\nPlease verify email address and resend manually if needed.",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 979. EVENT INVITE FOR VIP CUSTOMERS (7 nodes)
  // ===================================================================

  "Event Invite for VIP Customers": {
    summary:
      "Triggers for VIP segments, selects VIP customers from HubSpot, composes exclusive AI invitations, generates personalized landing pages, dispatches invite emails, tracks RSVPs with a delay and condition, and logs results to Sheets.",
    nodes: [
      trigger("Schedule: VIP event invite campaign", "schedule", {
        schedule: "0 10 * * 1 (Monday at 10 AM)",
        howItStarts:
          "A weekly scheduled job identifies upcoming VIP-eligible events and initiates the exclusive invitation flow for high-value customers in the CRM",
        setup:
          "Configure HubSpot API for VIP customer queries. Provide OpenAI API key for personalized invitations. Define VIP criteria (deal amount, lifecycle stage, engagement score). Set RSVP tracking parameters.",
        inputFields: [],
      }),

      // 2. VIP customer selector (HubSpot)
      hubspotGetNode(
        "2",
        "Fetch VIP customer segment from HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          { name: "results", customName: "vip_contacts_json", type: "string" },
          { name: "total", customName: "total_vip_contacts", type: "number" },
        ],
      ),

      // 3. AI exclusive invite
      aiNode(
        "3",
        "AI: Compose exclusive VIP event invitation",
        520,
        "You are an executive event strategist. Write exclusive, high-touch event invitations that make VIP customers feel valued. Use language that conveys exclusivity and personal attention. Return JSON.",
        "Create an exclusive VIP event invitation. VIP contacts found: {total_vip_contacts}. Write invite_subject (exclusive tone, under 60 chars), invite_body_html (personalized VIP language, event details placeholder, exclusive benefits, limited availability, RSVP CTA), landing_page_headline (for personalized landing page), landing_page_cta (button text), and exclusivity_hook (one sentence on why this is VIP-only).",
        [
          { field: "invite_subject", as: "vip_invite_subject", type: "string" },
          { field: "invite_body_html", as: "vip_invite_body", type: "string" },
          {
            field: "landing_page_headline",
            as: "vip_landing_headline",
            type: "string",
          },
          { field: "landing_page_cta", as: "vip_landing_cta", type: "string" },
          { field: "exclusivity_hook", as: "exclusivity_hook", type: "string" },
        ],
      ),

      // 4. Personalized landing page generation (runCode)
      n("4", "runCode", "Generate personalized VIP landing page URL", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate personalized landing page URL with VIP parameters
const headline = encodeURIComponent(vars.vip_landing_headline || "");
const cta = encodeURIComponent(vars.vip_landing_cta || "RSVP Now");
const eventId = "evt_" + Date.now();
const rsvpDeadline = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
return {
  vip_landing_url: "{app_base_url}/vip-event?event=" + eventId + "&headline=" + headline + "&cta=" + cta,
  vip_event_id: eventId,
  rsvp_deadline: rsvpDeadline,
  rsvp_tracking_url: "{app_base_url}/api/rsvp/" + eventId,
  total_invites_to_send: parseInt(vars.total_vip_contacts) || 0,
  landing_page_ready: "yes"
};`,
          outputVariables: [
            { name: "vip_landing_url", type: "string" },
            { name: "vip_event_id", type: "string" },
            { name: "rsvp_deadline", type: "string" },
            { name: "rsvp_tracking_url", type: "string" },
            { name: "total_invites_to_send", type: "number" },
            { name: "landing_page_ready", type: "string" },
          ],
        },
      }),

      // 5. Invite email dispatch
      emailNode(
        "5",
        "Send exclusive VIP event invitation email",
        1000,
        "{vip_contact_email}",
        "{vip_invite_subject}",
        "{vip_invite_body}",
      ),

      // 6. RSVP tracker (delay + condition)
      n("6", "delayAction", "Wait for RSVP responses", 1240, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
          description:
            "Wait 5 days for VIP customers to respond to the event invitation before sending a follow-up reminder to non-responders.",
        },
      }),

      // 7. RSVP results logger (Sheets)
      sheetsNode(
        "7",
        "Log VIP invite results (Sheets)",
        1480,
        "VIPEventInvites",
        '[["{vip_event_id}","{total_invites_to_send}","{rsvp_deadline}","{vip_invite_subject}","{exclusivity_hook}","{vip_landing_url}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 980. EVENT NO-SHOW FOLLOW-UP (6 nodes)
  // ===================================================================

  "Event No-Show Follow-Up": {
    summary:
      "Reconciles post-event attendance, segments no-shows with runCode, generates AI follow-up emails, dispatches them, logs no-show data to HubSpot CRM, and evaluates re-engagement offers via condition.",
    nodes: [
      trigger("Webhook: Post-event attendance reconciliation", "webhook_app", {
        source: "event_platform",
        event: "event.attendance_reconciled",
        howItStarts:
          "After an event concludes, the attendance reconciliation webhook fires with the full attendee list and their check-in status, identifying no-shows for follow-up",
        setup:
          "Configure event platform to send attendance reconciliation data after events end. Provide HubSpot API for CRM updates. Set up follow-up email templates and re-engagement offer rules.",
        inputFields: [],
      }),

      // 2. No-show segmentation (runCode)
      n("2", "runCode", "Segment no-show attendees from attendance data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse attendance data and segment no-shows
const payload = JSON.parse(vars.webhook_payload || "{}");
const attendees = payload.attendees || [];
const eventName = payload.event_name || payload.name || "Recent Event";
const eventDate = payload.event_date || new Date().toISOString().split("T")[0];
const noShows = attendees.filter(a => a.checked_in === false || a.status === "no_show" || a.attended === false);
const attended = attendees.filter(a => a.checked_in === true || a.status === "attended" || a.attended === true);
const firstNoShow = noShows.length > 0 ? noShows[0] : {};
return {
  noshow_event_name: eventName,
  noshow_event_date: eventDate,
  total_registered: attendees.length,
  total_attended: attended.length,
  total_no_shows: noShows.length,
  no_show_rate: attendees.length > 0 ? Math.round(noShows.length / attendees.length * 100) : 0,
  first_noshow_email: firstNoShow.email || "",
  first_noshow_name: firstNoShow.name || firstNoShow.first_name || "Attendee",
  no_shows_json: JSON.stringify(noShows.slice(0, 50)),
  has_no_shows: noShows.length > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "noshow_event_name", type: "string" },
            { name: "noshow_event_date", type: "string" },
            { name: "total_registered", type: "number" },
            { name: "total_attended", type: "number" },
            { name: "total_no_shows", type: "number" },
            { name: "no_show_rate", type: "number" },
            { name: "first_noshow_email", type: "string" },
            { name: "first_noshow_name", type: "string" },
            { name: "no_shows_json", type: "string" },
            { name: "has_no_shows", type: "string" },
          ],
        },
      }),

      // 3. AI follow-up email
      aiNode(
        "3",
        "AI: Compose no-show follow-up email",
        520,
        "You are an event follow-up specialist. Write empathetic, non-judgmental follow-up emails for event no-shows. Acknowledge they missed it without guilt-tripping. Offer value (recordings, resources). Return JSON.",
        "Write a follow-up email for an event no-show. Event: {noshow_event_name}. Date: {noshow_event_date}. Attendee: {first_noshow_name}. Total no-shows: {total_no_shows}. Return email_subject (empathetic, under 60 chars), email_body_html (we missed you tone, event highlights they missed, recording/resource links, next event CTA), and re_engagement_offer (specific offer to bring them to the next event).",
        [
          {
            field: "email_subject",
            as: "noshow_email_subject",
            type: "string",
          },
          { field: "email_body_html", as: "noshow_email_body", type: "string" },
          {
            field: "re_engagement_offer",
            as: "re_engagement_offer",
            type: "string",
          },
        ],
      ),

      // 4. Follow-up email dispatch
      emailNode(
        "4",
        "Send no-show follow-up email",
        760,
        "{first_noshow_email}",
        "{noshow_email_subject}",
        "{noshow_email_body}",
      ),

      // 5. CRM no-show logger (HubSpot)
      hubspotUpdateNode(
        "5",
        "Log no-show status in HubSpot CRM",
        1000,
        '{"last_event_status":"no_show","last_event_name":"{noshow_event_name}","last_event_date":"{noshow_event_date}","no_show_count_increment":"1","re_engagement_offer":"{re_engagement_offer}"}',
      ),

      // 6. Re-engagement offer condition
      n(
        "6",
        "condition",
        "Is no-show rate above threshold for extra outreach?",
        1240,
        {
          conditionData: [
            {
              variable: "no_show_rate",
              value: "30",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "4", "output_1"), // high no-show rate -> send more follow-ups
    ],
  },

  // ===================================================================
  // 981. EVENT NO-SHOW RE-ENGAGEMENT (7 nodes)
  // ===================================================================

  "Event No-Show Re-Engagement": {
    summary:
      "Detects no-shows via webhook, fetches attendee context from HubSpot, generates AI re-engagement emails, packages recordings and resources, dispatches the re-engagement email, offers early registration for the next event, and tracks patterns in Sheets.",
    nodes: [
      trigger("Webhook: No-show detected for re-engagement", "webhook_app", {
        source: "event_platform",
        event: "attendee.no_show",
        howItStarts:
          "A no-show is detected after the event attendance window closes, triggering a re-engagement flow that provides event recordings, resources, and early access to the next event",
        setup:
          "Configure event platform no-show detection webhook. Provide HubSpot API for attendee context. Set up recording hosting URLs. Define early registration incentive parameters.",
        inputFields: [],
      }),

      // 2. Attendee context fetch (HubSpot)
      hubspotGetNode(
        "2",
        "Fetch no-show attendee context from HubSpot",
        280,
        "crm/v3/objects/contacts/{contact_id}?properties=firstname,lastname,email,company,jobtitle,hs_analytics_num_visits,notes_last_updated",
        [
          {
            name: "properties.firstname",
            customName: "noshow_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "noshow_last_name",
            type: "string",
          },
          {
            name: "properties.email",
            customName: "noshow_email",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "noshow_company",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "noshow_job_title",
            type: "string",
          },
        ],
      ),

      // 3. AI re-engagement email
      aiNode(
        "3",
        "AI: Compose re-engagement email with resources",
        520,
        "You are a customer re-engagement specialist for events. Write warm, value-packed emails that turn no-shows into engaged attendees for future events. Focus on what they can still gain. Never guilt-trip. Return JSON.",
        "Write a re-engagement email for an event no-show. Name: {noshow_first_name} {noshow_last_name}. Company: {noshow_company}. Title: {noshow_job_title}. Return email_subject (value-focused, under 60 chars), email_body_html (what they missed, recording access, key takeaways, early bird offer for next event, personal CTA), resource_summary (bullet points of available resources), and early_bird_discount (percentage or offer for next event).",
        [
          {
            field: "email_subject",
            as: "reengage_email_subject",
            type: "string",
          },
          {
            field: "email_body_html",
            as: "reengage_email_body",
            type: "string",
          },
          { field: "resource_summary", as: "resource_summary", type: "string" },
          {
            field: "early_bird_discount",
            as: "early_bird_offer",
            type: "string",
          },
        ],
      ),

      // 4. Recording + resource packager (runCode)
      n("4", "runCode", "Package recording and resource links", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build resource package for the no-show attendee
const resourceSummary = vars.resource_summary || "";
const earlyBird = vars.early_bird_offer || "10% off";
const eventRecordingUrl = "{recording_base_url}/events/{event_id}/recording";
const slideDeckUrl = "{recording_base_url}/events/{event_id}/slides";
const resourcePackage = {
  recording_url: eventRecordingUrl,
  slide_deck_url: slideDeckUrl,
  resource_summary: resourceSummary,
  early_bird_code: "EARLYBIRD_" + Date.now().toString(36).toUpperCase(),
  early_bird_offer: earlyBird,
  package_expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
};
return {
  recording_url: resourcePackage.recording_url,
  slide_deck_url: resourcePackage.slide_deck_url,
  early_bird_code: resourcePackage.early_bird_code,
  package_expiry: resourcePackage.package_expires,
  resource_package_json: JSON.stringify(resourcePackage),
  package_ready: "yes"
};`,
          outputVariables: [
            { name: "recording_url", type: "string" },
            { name: "slide_deck_url", type: "string" },
            { name: "early_bird_code", type: "string" },
            { name: "package_expiry", type: "string" },
            { name: "resource_package_json", type: "string" },
            { name: "package_ready", type: "string" },
          ],
        },
      }),

      // 5. Re-engagement email dispatch
      emailNode(
        "5",
        "Send re-engagement email with resources",
        1000,
        "{noshow_email}",
        "{reengage_email_subject}",
        "{reengage_email_body}",
      ),

      // 6. Next-event early registration
      n(
        "6",
        "hitApi",
        "Register no-show for early access to next event",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "{event_api_url}/early-access/register",
            headers: [
              { key: "Authorization", value: "Bearer {event_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "email", value: "{noshow_email}" },
              { key: "name", value: "{noshow_first_name} {noshow_last_name}" },
              { key: "discount_code", value: "{early_bird_code}" },
              { key: "source", value: "no_show_re_engagement" },
            ],
            variables: [
              {
                name: "registration_id",
                customName: "early_reg_id",
                type: "string",
              },
              {
                name: "status",
                customName: "early_reg_status",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Engagement pattern tracker (Sheets)
      sheetsNode(
        "7",
        "Log no-show re-engagement pattern (Sheets)",
        1480,
        "NoShowReEngagement",
        '[["{noshow_email}","{noshow_first_name} {noshow_last_name}","{noshow_company}","{resource_summary}","{early_bird_code}","{early_reg_id}","{package_expiry}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 982. EVENT PROMOTION DRIP (9 nodes)
  // ===================================================================

  "Event Promotion Drip": {
    summary:
      "Kicks off an event promotion campaign, builds the target audience, generates an AI email series, sends announcement email #1, checks registration, sends speaker spotlight email #2 after a delay, boosts with SMS, sends a last-chance email #3, and logs drip performance to Sheets.",
    nodes: [
      trigger("Schedule: Event promotion drip kickoff", "schedule", {
        schedule: "0 9 * * 1 (Monday at 9 AM, 4 weeks before event)",
        howItStarts:
          "A scheduled campaign kicks off the multi-touch event promotion drip sequence, starting 4 weeks before the event date with escalating urgency across email and SMS",
        setup:
          "Configure event details (date, venue, speakers, registration URL). Provide Twilio for SMS. Set up audience segment criteria. Define drip timing: Week 1 announcement, Week 2 speakers, Week 3 SMS boost, Week 4 last chance.",
        inputFields: [],
      }),

      // 2. Target audience builder (runCode)
      n("2", "runCode", "Build target audience for event promotion", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Define event promotion parameters and audience criteria
const eventName = "Upcoming Event";
const eventDate = new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
const registrationUrl = "{app_base_url}/events/register";
const earlyBirdDeadline = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
return {
  drip_event_name: eventName,
  drip_event_date: eventDate,
  registration_url: registrationUrl,
  early_bird_deadline: earlyBirdDeadline,
  drip_campaign_id: "drip_" + Date.now(),
  total_audience_size: 0,
  drip_phase: "announcement",
  weeks_until_event: 4
};`,
          outputVariables: [
            { name: "drip_event_name", type: "string" },
            { name: "drip_event_date", type: "string" },
            { name: "registration_url", type: "string" },
            { name: "early_bird_deadline", type: "string" },
            { name: "drip_campaign_id", type: "string" },
            { name: "total_audience_size", type: "number" },
            { name: "drip_phase", type: "string" },
            { name: "weeks_until_event", type: "number" },
          ],
        },
      }),

      // 3. AI email series generation
      aiNode(
        "3",
        "AI: Generate 3-part event promotion email series",
        520,
        "You are an event marketing copywriter. Create a 3-email drip sequence with escalating urgency. Email 1: excitement and announcement. Email 2: speaker spotlights and social proof. Email 3: last chance with scarcity. Return JSON.",
        "Create a 3-email event promotion drip. Event: {drip_event_name}. Date: {drip_event_date}. Registration: {registration_url}. Early bird deadline: {early_bird_deadline}. Return email1_subject, email1_body_html (announcement, excitement), email2_subject, email2_body_html (speakers, social proof), email3_subject, email3_body_html (last chance, scarcity), and sms_urgency_message (max 160 chars, week 3 boost).",
        [
          {
            field: "email1_subject",
            as: "drip_email1_subject",
            type: "string",
          },
          { field: "email1_body_html", as: "drip_email1_body", type: "string" },
          {
            field: "email2_subject",
            as: "drip_email2_subject",
            type: "string",
          },
          { field: "email2_body_html", as: "drip_email2_body", type: "string" },
          {
            field: "email3_subject",
            as: "drip_email3_subject",
            type: "string",
          },
          { field: "email3_body_html", as: "drip_email3_body", type: "string" },
          {
            field: "sms_urgency_message",
            as: "drip_sms_message",
            type: "string",
          },
        ],
      ),

      // 4. Email #1 announcement
      emailNode(
        "4",
        "Send Email #1: Event announcement",
        760,
        "{subscriber_email}",
        "{drip_email1_subject}",
        "{drip_email1_body}",
      ),

      // 5. Registration check condition
      n("5", "condition", "Has the recipient already registered?", 1000, {
        conditionData: [
          {
            variable: "already_registered",
            value: "true",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Email #2 speaker spotlight (delay)
      n("6", "delayAction", "Wait 7 days for Email #2", 1240, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Wait 7 days after the announcement email before sending the speaker spotlight email. This gives recipients time to digest the initial message.",
        },
      }),

      // 7. SMS urgency boost (Twilio)
      smsNode(
        "7",
        "Send SMS urgency boost (Twilio)",
        1480,
        "{subscriber_phone}",
        "{drip_sms_message}",
      ),

      // 8. Email #3 last chance
      emailNode(
        "8",
        "Send Email #3: Last chance to register",
        1720,
        "{subscriber_email}",
        "{drip_email3_subject}",
        "{drip_email3_body}",
      ),

      // 9. Drip performance logger (Sheets)
      sheetsNode(
        "9",
        "Log drip campaign performance (Sheets)",
        1960,
        "EventDripPerformance",
        '[["{drip_campaign_id}","{drip_event_name}","{drip_event_date}","{total_audience_size}","{drip_email1_subject}","{drip_email2_subject}","{drip_email3_subject}","completed","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // not registered -> continue drip
      e("5", "9", "output_2"), // already registered -> skip to log
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 983. EVENT PROMOTION MULTI CHANNEL CAMPAIGN (10 nodes)
  // ===================================================================

  "Event Promotion Multi Channel Campaign": {
    summary:
      "Initializes a multi-channel campaign, segments the audience, generates AI content for all channels, runs parallel email, SMS, push notification, and social tracks, coordinates timing, tracks registrations in real-time, and reports campaign analytics to Sheets.",
    nodes: [
      trigger("Schedule: Multi-channel event campaign launch", "schedule", {
        schedule: "0 8 * * 1 (Monday at 8 AM, campaign launch day)",
        howItStarts:
          "A scheduled job launches a coordinated multi-channel event promotion campaign across email, SMS, push notifications, and social media simultaneously",
        setup:
          "Configure all channel credentials: SendGrid for email, Twilio for SMS, OneSignal for push, Buffer for social. Set up audience segments. Define campaign timing and channel-specific content rules.",
        inputFields: [],
      }),

      // 2. Audience segmentation (runCode)
      n("2", "runCode", "Segment audience by channel preference", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Define campaign parameters and segment by channel preference
const campaignId = "mcc_" + Date.now();
const eventName = "Multi-Channel Event Campaign";
const eventDate = new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
return {
  mc_campaign_id: campaignId,
  mc_event_name: eventName,
  mc_event_date: eventDate,
  email_segment_size: 0,
  sms_segment_size: 0,
  push_segment_size: 0,
  social_audience_size: 0,
  total_reach: 0,
  campaign_status: "launched",
  registration_goal: 500
};`,
          outputVariables: [
            { name: "mc_campaign_id", type: "string" },
            { name: "mc_event_name", type: "string" },
            { name: "mc_event_date", type: "string" },
            { name: "email_segment_size", type: "number" },
            { name: "sms_segment_size", type: "number" },
            { name: "push_segment_size", type: "number" },
            { name: "social_audience_size", type: "number" },
            { name: "total_reach", type: "number" },
            { name: "campaign_status", type: "string" },
            { name: "registration_goal", type: "number" },
          ],
        },
      }),

      // 3. AI multi-format content
      aiNode(
        "3",
        "AI: Generate content for all channels",
        520,
        "You are a multi-channel marketing content strategist. Create channel-optimized content for the same event across email, SMS, push notifications, and social media. Each channel has different constraints and tone. Return JSON.",
        "Create multi-channel content for event: {mc_event_name}. Date: {mc_event_date}. Campaign: {mc_campaign_id}. Goal: {registration_goal} registrations. Return email_subject, email_body_html, sms_text (max 160 chars), push_title (max 50 chars), push_body (max 100 chars), social_post (max 280 chars with hashtags), and content_theme (unifying theme across channels).",
        [
          { field: "email_subject", as: "mc_email_subject", type: "string" },
          { field: "email_body_html", as: "mc_email_body", type: "string" },
          { field: "sms_text", as: "mc_sms_text", type: "string" },
          { field: "push_title", as: "mc_push_title", type: "string" },
          { field: "push_body", as: "mc_push_body", type: "string" },
          { field: "social_post", as: "mc_social_post", type: "string" },
          { field: "content_theme", as: "mc_content_theme", type: "string" },
        ],
      ),

      // 4. Email campaign track
      emailNode(
        "4",
        "Send email campaign track",
        760,
        "{subscriber_email}",
        "{mc_email_subject}",
        "{mc_email_body}",
      ),

      // 5. SMS campaign track (Twilio)
      smsNode(
        "5",
        "Send SMS campaign track (Twilio)",
        1000,
        "{subscriber_phone}",
        "{mc_sms_text}",
      ),

      // 6. Push notification track
      n(
        "6",
        "pushNotification",
        "Send push notification track",
        1240,
        {
          pushNotificationDetails: {
            title: "{mc_push_title}",
            body: "{mc_push_body}",
            targetAudience: "event_interested_segment",
            data: '{"campaign_id":"{mc_campaign_id}","event_date":"{mc_event_date}","channel":"push"}',
          },
        },
        400,
      ),

      // 7. Social media track (Buffer)
      bufferNode(
        "7",
        "Buffer: Schedule social media post",
        1480,
        "{mc_social_post}",
      ),

      // 8. Channel coordination (runCode)
      n("8", "runCode", "Coordinate cross-channel timing and dedup", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Coordinate cross-channel delivery and track status
const channels = {
  email: { sent: true, segment_size: vars.email_segment_size || 0 },
  sms: { sent: true, segment_size: vars.sms_segment_size || 0 },
  push: { sent: true, segment_size: vars.push_segment_size || 0 },
  social: { sent: true, segment_size: vars.social_audience_size || 0 }
};
const totalSent = Object.values(channels).reduce((sum, ch) => sum + ch.segment_size, 0);
const channelsActive = Object.entries(channels).filter(([_, v]) => v.sent).map(([k]) => k);
return {
  channels_activated: channelsActive.join(", "),
  total_messages_sent: totalSent,
  coordination_status: "all_channels_dispatched",
  coordination_timestamp: new Date().toISOString(),
  dedup_applied: "yes"
};`,
          outputVariables: [
            { name: "channels_activated", type: "string" },
            { name: "total_messages_sent", type: "number" },
            { name: "coordination_status", type: "string" },
            { name: "coordination_timestamp", type: "string" },
            { name: "dedup_applied", type: "string" },
          ],
        },
      }),

      // 9. Real-time registration tracker
      n("9", "hitApi", "Check real-time registration count", 1960, {
        hitApiDetails: {
          method: "GET",
          url: "{event_api_url}/events/{event_id}/registrations/count",
          headers: [
            { key: "Authorization", value: "Bearer {event_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "count",
              customName: "current_registrations",
              type: "number",
            },
            {
              name: "goal_percent",
              customName: "goal_progress_percent",
              type: "number",
            },
          ],
        },
      }),

      // 10. Campaign analytics report (Sheets)
      sheetsNode(
        "10",
        "Log campaign analytics report (Sheets)",
        2200,
        "MultiChannelCampaigns",
        '[["{mc_campaign_id}","{mc_event_name}","{mc_event_date}","{channels_activated}","{total_messages_sent}","{current_registrations}","{registration_goal}","{goal_progress_percent}","{mc_content_theme}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 984. EVENT REGISTRATION CONFIRMATION (6 nodes)
  // ===================================================================

  "Event Registration Confirmation": {
    summary:
      "Triggers on event registration, generates a calendar invite with runCode, composes a personalized AI confirmation email, dispatches it, logs the event to HubSpot CRM, and handles delivery failures.",
    nodes: [
      trigger("Webhook: Event registration completed", "webhook_app", {
        source: "event_platform",
        event: "registration.completed",
        howItStarts:
          "A new event registration is completed, triggering an immediate confirmation flow that sends a calendar invite, personalized confirmation email, and logs the registration in CRM",
        setup:
          "Configure event platform webhook for registration events. Provide HubSpot API for CRM logging. Set up calendar invite generation parameters (ICS format).",
        inputFields: [],
      }),

      // 2. Calendar invite generation (runCode)
      n("2", "runCode", "Generate ICS calendar invite", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse registration and generate calendar invite details
const payload = JSON.parse(vars.webhook_payload || "{}");
const attendeeName = payload.name || payload.attendee_name || payload.first_name || "Attendee";
const attendeeEmail = payload.email || payload.attendee_email || "";
const eventName = payload.event_name || payload.title || "Upcoming Event";
const eventDate = payload.event_date || payload.date || "";
const eventTime = payload.event_time || payload.start_time || "10:00";
const eventLocation = payload.location || payload.venue || "Virtual";
const eventUrl = payload.event_url || payload.join_link || "";
const ticketType = payload.ticket_type || "general";
const registrationId = payload.registration_id || "reg_" + Date.now();
// Generate ICS content
const icsContent = "BEGIN:VCALENDAR\\nVERSION:2.0\\nBEGIN:VEVENT\\nSUMMARY:" + eventName + "\\nDTSTART:" + eventDate.replace(/-/g, "") + "T" + eventTime.replace(/:/g, "") + "00\\nLOCATION:" + eventLocation + "\\nDESCRIPTION:Join link: " + eventUrl + "\\nEND:VEVENT\\nEND:VCALENDAR";
return {
  reg_attendee_name: attendeeName,
  reg_attendee_email: attendeeEmail,
  reg_event_name: eventName,
  reg_event_date: eventDate,
  reg_event_time: eventTime,
  reg_event_location: eventLocation,
  reg_event_url: eventUrl,
  reg_ticket_type: ticketType,
  reg_id: registrationId,
  calendar_ics: icsContent,
  calendar_ready: "yes"
};`,
          outputVariables: [
            { name: "reg_attendee_name", type: "string" },
            { name: "reg_attendee_email", type: "string" },
            { name: "reg_event_name", type: "string" },
            { name: "reg_event_date", type: "string" },
            { name: "reg_event_time", type: "string" },
            { name: "reg_event_location", type: "string" },
            { name: "reg_event_url", type: "string" },
            { name: "reg_ticket_type", type: "string" },
            { name: "reg_id", type: "string" },
            { name: "calendar_ics", type: "string" },
            { name: "calendar_ready", type: "string" },
          ],
        },
      }),

      // 3. AI confirmation email
      aiNode(
        "3",
        "AI: Compose personalized confirmation email",
        520,
        "You are an event communications specialist. Write polished confirmation emails that build excitement and provide all essential details. Include pre-event preparation tips. Return JSON.",
        "Write a registration confirmation email. Attendee: {reg_attendee_name}. Event: {reg_event_name}. Date: {reg_event_date} at {reg_event_time}. Location: {reg_event_location}. Join URL: {reg_event_url}. Ticket: {reg_ticket_type}. Reg ID: {reg_id}. Return email_subject (confirming + exciting), email_body_html (confirmation details, what to expect, preparation tips, calendar reminder, support contact), and pre_event_tip (one actionable tip to prepare).",
        [
          {
            field: "email_subject",
            as: "confirm_email_subject",
            type: "string",
          },
          {
            field: "email_body_html",
            as: "confirm_email_body",
            type: "string",
          },
          { field: "pre_event_tip", as: "pre_event_tip", type: "string" },
        ],
      ),

      // 4. Confirmation email dispatch
      emailNode(
        "4",
        "Send registration confirmation email",
        760,
        "{reg_attendee_email}",
        "{confirm_email_subject}",
        "{confirm_email_body}",
      ),

      // 5. CRM event logger (HubSpot)
      hubspotUpdateNode(
        "5",
        "Log event registration in HubSpot CRM",
        1000,
        '{"last_event_registered":"{reg_event_name}","registration_date":"{reg_event_date}","registration_id":"{reg_id}","ticket_type":"{reg_ticket_type}","event_attendee":"true"}',
      ),

      // 6. Delivery failure handler (condition)
      n(
        "6",
        "condition",
        "Was the confirmation email delivered successfully?",
        1240,
        {
          conditionData: [
            {
              variable: "calendar_ready",
              value: "yes",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "4", "output_2"), // delivery failed -> retry send
    ],
  },

  // ===================================================================
  // 985. EXIT-INTENT EMAIL-ONLY CAPTURE (6 nodes)
  // ===================================================================

  "Exit-Intent Email-Only Capture": {
    summary:
      "Triggers on exit-intent events, evaluates visitor context with runCode, selects a dynamic offer, renders a popup via sendMessage, captures email and consent in HubSpot, and triggers an immediate welcome sequence.",
    nodes: [
      trigger("Webhook: Exit-intent detected", "webhook_app", {
        source: "website",
        event: "visitor.exit_intent",
        howItStarts:
          "The website detects exit-intent behavior (mouse moving toward browser close/back, prolonged inactivity, or tab switching) and fires a webhook with visitor context for email capture",
        setup:
          "Configure exit-intent detection on the website. Set up dynamic offer rules based on visitor behavior. Provide HubSpot API for contact creation. Define popup templates and consent language.",
        inputFields: [],
      }),

      // 2. Visitor context evaluation (runCode)
      n("2", "runCode", "Evaluate visitor context for offer selection", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse exit-intent event and evaluate visitor for capture eligibility
const payload = JSON.parse(vars.webhook_payload || "{}");
const pageUrl = payload.page_url || payload.url || "";
const pageTitle = payload.page_title || "";
const timeOnSite = payload.time_on_site || payload.session_duration || 0;
const pagesViewed = payload.pages_viewed || payload.page_count || 1;
const referrer = payload.referrer || "direct";
const isReturning = payload.is_returning || false;
const cartValue = payload.cart_value || 0;
// Determine capture eligibility
const minTimeThreshold = 10; // seconds
const eligible = timeOnSite >= minTimeThreshold && !payload.already_subscribed;
// Determine visitor interest level
const interestLevel = pagesViewed >= 5 ? "high" : pagesViewed >= 3 ? "medium" : "low";
return {
  exit_page_url: pageUrl,
  exit_page_title: pageTitle,
  visitor_time_on_site: timeOnSite,
  visitor_pages_viewed: pagesViewed,
  visitor_referrer: referrer,
  visitor_is_returning: isReturning ? "yes" : "no",
  visitor_cart_value: cartValue,
  capture_eligible: eligible ? "yes" : "no",
  visitor_interest_level: interestLevel,
  visitor_session_id: payload.session_id || "sess_" + Date.now()
};`,
          outputVariables: [
            { name: "exit_page_url", type: "string" },
            { name: "exit_page_title", type: "string" },
            { name: "visitor_time_on_site", type: "number" },
            { name: "visitor_pages_viewed", type: "number" },
            { name: "visitor_referrer", type: "string" },
            { name: "visitor_is_returning", type: "string" },
            { name: "visitor_cart_value", type: "number" },
            { name: "capture_eligible", type: "string" },
            { name: "visitor_interest_level", type: "string" },
            { name: "visitor_session_id", type: "string" },
          ],
        },
      }),

      // 3. Dynamic offer selection (runCode)
      n("3", "runCode", "Select dynamic offer based on visitor context", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select the best offer based on visitor behavior
const interest = vars.visitor_interest_level || "low";
const cartValue = parseFloat(vars.visitor_cart_value) || 0;
const isReturning = vars.visitor_is_returning === "yes";
let offerType, offerHeadline, offerDescription, offerValue;
if (cartValue > 0) {
  offerType = "cart_saver";
  offerHeadline = "Wait! Your cart is worth saving";
  offerDescription = "Get an exclusive discount on your current cart. Enter your email and we will send you a special offer.";
  offerValue = cartValue > 100 ? "15% off" : "10% off";
} else if (interest === "high") {
  offerType = "content_upgrade";
  offerHeadline = "Before you go — get our exclusive guide";
  offerDescription = "You have been exploring our content. Get our premium guide delivered to your inbox.";
  offerValue = "Free premium guide";
} else if (isReturning) {
  offerType = "returning_offer";
  offerHeadline = "Welcome back! We have something for you";
  offerDescription = "As a returning visitor, get early access to our latest updates.";
  offerValue = "Early access";
} else {
  offerType = "newsletter";
  offerHeadline = "Stay in the loop";
  offerDescription = "Join our newsletter for the latest insights and updates delivered weekly.";
  offerValue = "Weekly insights";
}
return {
  offer_type: offerType,
  offer_headline: offerHeadline,
  offer_description: offerDescription,
  offer_value: offerValue,
  offer_selected: "yes"
};`,
          outputVariables: [
            { name: "offer_type", type: "string" },
            { name: "offer_headline", type: "string" },
            { name: "offer_description", type: "string" },
            { name: "offer_value", type: "string" },
            { name: "offer_selected", type: "string" },
          ],
        },
      }),

      // 4. Popup content renderer (sendMessage)
      n("4", "sendMessage", "Display exit-intent email capture popup", 760, {
        sendMessageDetails: {
          conversationId: "{visitor_session_id}",
          message:
            "{offer_headline}\\n\\n{offer_description}\\n\\nOffer: {offer_value}\\n\\nEnter your email to claim this offer.",
        },
      }),

      // 5. Email capture + consent logger (HubSpot)
      hubspotUpdateNode(
        "5",
        "Create contact + log consent in HubSpot",
        1000,
        '{"email":"{captured_email}","exit_intent_offer":"{offer_type}","offer_value":"{offer_value}","capture_source":"exit_intent","consent_given":"true","consent_date":"{capture_timestamp}","interest_level":"{visitor_interest_level}"}',
      ),

      // 6. Immediate welcome trigger
      emailNode(
        "6",
        "Send immediate welcome email",
        1240,
        "{captured_email}",
        "Welcome! Here is your {offer_value}",
        "Hi there,\\n\\nThank you for subscribing! As promised, here is your {offer_value}.\\n\\n{offer_description}\\n\\nWe are excited to have you join our community. Expect great content in your inbox soon.\\n\\nBest regards,\\n{company_name}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 986. EXIT-INTENT SMS AND EMAIL CAPTURE (7 nodes)
  // ===================================================================

  "Exit-Intent SMS and Email Capture": {
    summary:
      "Triggers on exit-intent, checks visitor eligibility, displays a dual-capture popup, handles form submissions, logs consent and compliance to Sheets, sends immediate dual welcome messages (email + SMS), and handles validation errors.",
    nodes: [
      trigger("Webhook: Exit-intent for dual capture", "webhook_app", {
        source: "website",
        event: "visitor.exit_intent_dual",
        howItStarts:
          "Exit-intent is detected on a high-value page (pricing, product, or checkout), triggering a dual-capture popup that collects both email and SMS consent in a single interaction",
        setup:
          "Configure exit-intent detection on high-value pages. Define dual-capture popup template with separate consent checkboxes for email and SMS. Set up compliance logging for TCPA/GDPR. Provide Twilio for SMS.",
        inputFields: [],
      }),

      // 2. Visitor eligibility check (runCode)
      n("2", "runCode", "Check visitor eligibility for dual capture", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate visitor for dual-capture eligibility
const payload = JSON.parse(vars.webhook_payload || "{}");
const pageUrl = payload.page_url || "";
const timeOnSite = payload.time_on_site || 0;
const isHighValuePage = /pricing|product|checkout|demo/i.test(pageUrl);
const hasExistingEmail = payload.already_subscribed_email || false;
const hasExistingSMS = payload.already_subscribed_sms || false;
const eligible = timeOnSite >= 15 && isHighValuePage && !hasExistingEmail;
return {
  dual_page_url: pageUrl,
  dual_time_on_site: timeOnSite,
  is_high_value_page: isHighValuePage ? "yes" : "no",
  dual_capture_eligible: eligible ? "yes" : "no",
  needs_email: hasExistingEmail ? "no" : "yes",
  needs_sms: hasExistingSMS ? "no" : "yes",
  dual_session_id: payload.session_id || "dual_" + Date.now()
};`,
          outputVariables: [
            { name: "dual_page_url", type: "string" },
            { name: "dual_time_on_site", type: "number" },
            { name: "is_high_value_page", type: "string" },
            { name: "dual_capture_eligible", type: "string" },
            { name: "needs_email", type: "string" },
            { name: "needs_sms", type: "string" },
            { name: "dual_session_id", type: "string" },
          ],
        },
      }),

      // 3. Dual-capture popup (sendMessage)
      n("3", "sendMessage", "Display dual email + SMS capture popup", 520, {
        sendMessageDetails: {
          conversationId: "{dual_session_id}",
          message:
            "Before you go — unlock exclusive access!\\n\\nGet instant alerts on deals and updates via SMS, plus weekly insights via email.\\n\\nEmail: [input field]\\nPhone: [input field]\\n\\n[x] I consent to receive marketing emails\\n[x] I consent to receive SMS messages (msg & data rates may apply, reply STOP to cancel)\\n\\nClaim My Access",
        },
      }),

      // 4. Form submission handler (runCode)
      n(
        "4",
        "runCode",
        "Process and validate dual-capture form submission",
        760,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Validate form submission data
const email = (vars.submitted_email || "").trim().toLowerCase();
const phone = (vars.submitted_phone || "").trim().replace(/[^+\\d]/g, "");
const emailConsent = vars.email_consent === "true";
const smsConsent = vars.sms_consent === "true";
const emailValid = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(email);
const phoneValid = phone.length >= 10;
const hasValidSubmission = (emailValid && emailConsent) || (phoneValid && smsConsent);
return {
  validated_email: emailValid ? email : "",
  validated_phone: phoneValid ? phone : "",
  email_consent_given: emailConsent && emailValid ? "yes" : "no",
  sms_consent_given: smsConsent && phoneValid ? "yes" : "no",
  submission_valid: hasValidSubmission ? "yes" : "no",
  validation_errors: (!emailValid && emailConsent ? "invalid_email " : "") + (!phoneValid && smsConsent ? "invalid_phone" : "") || "none",
  consent_timestamp: new Date().toISOString()
};`,
            outputVariables: [
              { name: "validated_email", type: "string" },
              { name: "validated_phone", type: "string" },
              { name: "email_consent_given", type: "string" },
              { name: "sms_consent_given", type: "string" },
              { name: "submission_valid", type: "string" },
              { name: "validation_errors", type: "string" },
              { name: "consent_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 5. Consent + compliance logger (Sheets)
      sheetsNode(
        "5",
        "Log consent and compliance data (Sheets)",
        1000,
        "DualCaptureConsent",
        '[["{validated_email}","{validated_phone}","{email_consent_given}","{sms_consent_given}","{consent_timestamp}","{dual_page_url}","{dual_session_id}","{validation_errors}"]]',
      ),

      // 6. Immediate dual welcome (email + SMS)
      emailNode(
        "6",
        "Send welcome email + SMS",
        1240,
        "{validated_email}",
        "Welcome! You are in",
        "Hi there,\\n\\nThank you for signing up! You are now part of our community.\\n\\nYou will receive:\\n- Weekly insights via email\\n- Instant deal alerts via SMS\\n\\nWe value your trust and will never spam you.\\n\\nBest regards,\\n{company_name}",
      ),

      // 7. Validation error handler (condition)
      n("7", "condition", "Was the submission valid?", 1480, {
        conditionData: [
          {
            variable: "submission_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "3", "output_2"), // invalid -> re-show popup
    ],
  },

  // ===================================================================
  // 987. EXPANSION REVENUE EMAIL (7 nodes)
  // ===================================================================

  "Expansion Revenue Email": {
    summary:
      "Scans for expansion opportunities on a weekly schedule, matches cross-sell and upsell candidates with runCode, generates AI expansion emails, dispatches them, tracks engagement and responses, routes qualified leads to sales, and logs outcomes to Sheets.",
    nodes: [
      trigger("Schedule: Weekly expansion revenue scan", "schedule", {
        schedule: "0 7 * * 2 (Tuesday at 7 AM)",
        howItStarts:
          "A weekly scheduled scan identifies existing customers with expansion revenue potential based on usage patterns, contract renewal dates, and product adoption gaps",
        setup:
          "Configure CRM access for customer data and usage analytics. Provide OpenAI API for personalized expansion messaging. Define cross-sell/upsell rules and qualification thresholds. Set up sales handoff criteria.",
        inputFields: [],
      }),

      // 2. Cross-sell/upsell matcher (runCode)
      n("2", "runCode", "Match customers with expansion opportunities", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify expansion revenue opportunities
const scanDate = new Date().toISOString().split("T")[0];
const scanId = "exp_" + Date.now();
// In production, this would query CRM/usage data
// Define opportunity matching criteria
return {
  expansion_scan_id: scanId,
  scan_date: scanDate,
  total_customers_scanned: 0,
  cross_sell_opportunities: 0,
  upsell_opportunities: 0,
  total_opportunities: 0,
  top_opportunity_email: "",
  top_opportunity_name: "",
  top_opportunity_company: "",
  current_plan: "",
  recommended_upgrade: "",
  usage_percent: 0,
  contract_renewal_days: 0,
  expansion_type: "cross_sell",
  estimated_expansion_arr: 0
};`,
          outputVariables: [
            { name: "expansion_scan_id", type: "string" },
            { name: "scan_date", type: "string" },
            { name: "total_customers_scanned", type: "number" },
            { name: "cross_sell_opportunities", type: "number" },
            { name: "upsell_opportunities", type: "number" },
            { name: "total_opportunities", type: "number" },
            { name: "top_opportunity_email", type: "string" },
            { name: "top_opportunity_name", type: "string" },
            { name: "top_opportunity_company", type: "string" },
            { name: "current_plan", type: "string" },
            { name: "recommended_upgrade", type: "string" },
            { name: "usage_percent", type: "number" },
            { name: "contract_renewal_days", type: "number" },
            { name: "expansion_type", type: "string" },
            { name: "estimated_expansion_arr", type: "number" },
          ],
        },
      }),

      // 3. AI expansion email
      aiNode(
        "3",
        "AI: Compose personalized expansion email",
        520,
        "You are an account expansion specialist. Write helpful, consultative emails that show customers the value of expanding their usage. Focus on their specific situation and ROI. Never be pushy. Return JSON.",
        "Write an expansion email. Customer: {top_opportunity_name}. Company: {top_opportunity_company}. Current plan: {current_plan}. Usage: {usage_percent}%. Recommended: {recommended_upgrade}. Expansion type: {expansion_type}. Contract renewal in {contract_renewal_days} days. Estimated ARR increase: ${estimated_expansion_arr}. Return email_subject (consultative, under 60 chars), email_body_html (value-based, usage insights, upgrade benefits, ROI projection, soft CTA for a conversation), and key_value_prop (one sentence on the main benefit).",
        [
          {
            field: "email_subject",
            as: "expansion_email_subject",
            type: "string",
          },
          {
            field: "email_body_html",
            as: "expansion_email_body",
            type: "string",
          },
          { field: "key_value_prop", as: "key_value_prop", type: "string" },
        ],
      ),

      // 4. Email dispatch
      emailNode(
        "4",
        "Send expansion revenue email",
        760,
        "{top_opportunity_email}",
        "{expansion_email_subject}",
        "{expansion_email_body}",
      ),

      // 5. Engagement + response tracker (delay + check)
      n("5", "delayAction", "Wait for engagement and response tracking", 1000, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 72,
          delayUnit: "hours",
          description:
            "Wait 72 hours to track email opens, clicks, and replies before evaluating whether the opportunity should be handed off to sales for direct follow-up.",
        },
      }),

      // 6. Sales handoff condition
      n(
        "6",
        "condition",
        "Is this a qualified expansion opportunity for sales?",
        1240,
        {
          conditionData: [
            {
              variable: "estimated_expansion_arr",
              value: "5000",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Outcome logger (Sheets)
      sheetsNode(
        "7",
        "Log expansion campaign outcome (Sheets)",
        1480,
        "ExpansionRevenue",
        '[["{expansion_scan_id}","{scan_date}","{top_opportunity_email}","{top_opportunity_company}","{current_plan}","{recommended_upgrade}","{expansion_type}","{estimated_expansion_arr}","{key_value_prop}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // qualified -> log for sales handoff
      e("6", "7", "output_2"), // not qualified -> log as nurture
    ],
  },
};
